import React, { useEffect, useMemo, useRef } from 'react';
import { NavLink, useLocation } from 'react-router-dom';
import clsx from 'clsx';
import { useModuleNavigation } from '@/hooks/useModuleNavigation';
import { useAuth } from '@/contexts/AuthContext';
import { filterNavigationByRoles } from '../../layout.definitions';

/**
 * ModuleNavigationBar component
 *
 * Renders horizontal module sub-navigation in the main layout header.
 * Automatically shows/hides based on active module and filters items by user roles.
 *
 * @example
 * ```tsx
 * // In Layout.tsx header
 * <header className="sticky top-0 z-40">
 *   <div className="flex h-16 items-center">
 *     {/\* Existing header content *\/}
 *   </div>
 *   <ModuleNavigationBar />
 * </header>
 * ```
 */
export const ModuleNavigationBar: React.FC = () => {
  const { moduleNav } = useModuleNavigation();
  const { userHasRole } = useAuth();
  const location = useLocation();
  const activeItemRef = useRef<HTMLAnchorElement | null>(null);

  const filteredItems = useMemo(() => {
    if (!moduleNav?.items) return [];
    return filterNavigationByRoles(moduleNav.items, userHasRole);
  }, [moduleNav?.items, userHasRole]);

  useEffect(() => {
    const active = activeItemRef.current;
    if (!active || typeof active.scrollIntoView !== 'function') return;

    active.scrollIntoView({ inline: 'nearest', block: 'nearest' });
  }, [location.pathname]);

  if (!moduleNav?.isActive || filteredItems.length === 0) {
    return null;
  }

  return (
    <nav
      aria-label={`${moduleNav.displayName || moduleNav.moduleId} navigation`}
      className="border-t border-gray-200 bg-white"
      role="navigation"
    >
      <div className="flex gap-1 overflow-x-auto px-4 py-2 sm:gap-4 sm:px-6 lg:px-8">
        {filteredItems.map((item) => {
          const isActive = item.exact
            ? location.pathname === item.href
            : location.pathname.startsWith(item.href);

          const Icon = item.icon;

          return (
            <NavLink
              key={item.href}
              to={item.href}
              ref={isActive ? activeItemRef : undefined}
              className={clsx(
                  'flex shrink-0 items-center gap-2 whitespace-nowrap px-3 py-2 text-sm font-medium transition-colors',
                isActive
                  ? 'border-b-2 border-primary-600 text-primary-600'
                  : 'text-gray-600 hover:text-gray-900'
              )}
              aria-current={isActive ? 'page' : undefined}
            >
              {Icon && <Icon className="h-5 w-5" aria-hidden={true} />}
              <span>{item.name}</span>
              {item.badge !== undefined && (
                <span className="ml-auto rounded-full bg-primary-100 px-2 py-0.5 text-xs font-semibold text-primary-700">
                  {item.badge}
                </span>
              )}
            </NavLink>
          );
        })}
      </div>
    </nav>
  );
};
